/**
 * @file order-chain-tone.ts
 * @description Реестры тонов «Пути заказа»: состояние узла → рейл и цвет,
 * статус документа → тон бейджа, приоритет цвета полосы «Сейчас»
 * (phase19.24-order-card-chain-design.md §3, §14).
 *
 * Записи, а не каскад `if`: новый вид документа в цепочке = строка в реестре.
 * Цвета — только токены DS (`primary`, `warning`, `destructive`, поверхности),
 * никаких hex в классах.
 */

import type { DocumentStatusTone } from "../components/ui/DocumentStatusBadge";
import type {
  OrderChainNodeDto,
  OrderChainNodeKey,
  OrderChainNodeState,
} from "./order-chain";

/** Чем рисуется рейл узла: ● ◉ ○ ⨯ ⚠ из §3 — формами и иконками, не эмодзи. */
export type ChainRailShape = "filled" | "current" | "outline" | "failed" | "alert";

export interface NodeStateMeta {
  rail: ChainRailShape;
  /** Классы точки рейла (заливка/контур). */
  railClass: string;
  /** Фон строки узла. Текущий узел читается заодно с полосой «Сейчас». */
  rowClass: string;
  /** Приглушать ли текст: будущее и пропущенное — серым. */
  dimmed: boolean;
  /** Тон бейджа, если сервер не прислал свой. */
  fallbackTone: DocumentStatusTone;
}

/** Реестр состояний узла: новое состояние = запись здесь. */
export const CHAIN_NODE_STATE_META: Record<OrderChainNodeState, NodeStateMeta> = {
  done: {
    rail: "filled",
    railClass: "bg-primary",
    rowClass: "bg-surface-lowest",
    dimmed: false,
    fallbackTone: "done",
  },
  current: {
    rail: "current",
    railClass: "bg-primary ring-4 ring-primary/20",
    rowClass: "bg-primary/5",
    dimmed: false,
    fallbackTone: "active",
  },
  future: {
    rail: "outline",
    railClass: "bg-surface-lowest ring-2 ring-inset ring-on-surface/20",
    rowClass: "bg-surface-lowest",
    dimmed: true,
    fallbackTone: "draft",
  },
  failed: {
    rail: "failed",
    railClass: "text-destructive",
    rowClass: "bg-surface-lowest",
    dimmed: false,
    fallbackTone: "cancelled",
  },
  skipped: {
    rail: "failed",
    railClass: "text-on-surface-muted",
    rowClass: "bg-surface-lowest",
    dimmed: true,
    fallbackTone: "cancelled",
  },
  unavailable: {
    rail: "alert",
    railClass: "text-warning",
    rowClass: "bg-warning/8",
    dimmed: false,
    fallbackTone: "attention",
  },
};

/**
 * Метаданные состояния узла. Сервер вправе прислать состояние, которого в
 * union ещё нет (например `missing` у задания сборки, которого не создали):
 * такой узел рисуем как будущий — со своим серверным текстом и без падения
 * карточки. Подмены текста тут нет: hint приходит с сервера.
 */
export function nodeStateMeta(state: OrderChainNodeState): NodeStateMeta {
  return CHAIN_NODE_STATE_META[state] ?? CHAIN_NODE_STATE_META.future;
}

/** Известные тоны бейджа: чужое значение с сервера не красим наугад. */
const KNOWN_TONES: readonly DocumentStatusTone[] = [
  "draft",
  "active",
  "attention",
  "danger",
  "done",
  "cancelled",
];

/**
 * Зеркало серверного реестра тонов (§3) — держим на клиенте для сверки при
 * ревью и в тестах: если сервер начнёт красить «Платёж FAILED» как `cancelled`,
 * расхождение видно здесь, а не на скриншоте у пользователя. В рантайме тон
 * берётся из ответа (`statusTone`) — реестр не дублирует бизнес-логику.
 */
export const CHAIN_DOC_STATUS_TONE: Partial<
  Record<OrderChainNodeKey, Record<string, DocumentStatusTone>>
> = {
  INVOICE: { DRAFT: "draft", ISSUED: "attention", SIGNED: "done", VOIDED: "cancelled" },
  PAYMENT: {
    PENDING: "attention",
    COMPLETED: "done",
    REFUNDED: "cancelled",
    FAILED: "danger",
  },
  PICKING: {
    NEW: "active",
    IN_PROGRESS: "active",
    DONE: "done",
    SHORTAGE: "danger",
    CANCELLED: "cancelled",
  },
  SHIPMENT: {
    PENDING: "draft",
    SHIPPED: "active",
    IN_TRANSIT: "active",
    DELIVERED: "done",
    CANCELLED: "cancelled",
  },
  RETURN: {
    REQUESTED: "attention",
    APPROVED: "active",
    COMPLETED: "done",
    REJECTED: "cancelled",
  },
  AGREEMENT: {
    AWAITING_CALCULATION: "attention",
    AWAITING_CONFIRMATION: "active",
    CONFIRMED: "done",
    CANCELLED: "cancelled",
    OUTDATED: "cancelled",
  },
};

/** Тон бейджа узла: серверный, если он из закрытого списка, иначе по состоянию. */
export function resolveNodeTone(node: OrderChainNodeDto): DocumentStatusTone {
  if (KNOWN_TONES.includes(node.statusTone)) return node.statusTone;
  return nodeStateMeta(node.state).fallbackTone;
}

/** Тон полосы «Сейчас»: danger > primary (ваш ход) > цвет владельца (§14). */
export type NowStripTone = "danger" | "manager" | "owner";

export function nowStripTone(input: {
  isProblem: boolean;
  isOverdue: boolean;
  isManagerTurn: boolean;
}): NowStripTone {
  if (input.isProblem || input.isOverdue) return "danger";
  return input.isManagerTurn ? "manager" : "owner";
}
